import type { DemandVerdict } from '../types.ts';
import { DEMAND_VERDICT_LABEL, isHeadlineDemand, voicesLabel } from '../lib/demand.ts';

/**
 * A demand verdict as a legend key (`.key` geometry; design-system.md, "Demand annotations").
 *
 * `gap` is the whole point of the component's one decision: `wanted` takes the hot colour only
 * on a gap row, because signal is rationed to the finding the user ran the survey for. The caller
 * computes `gap` from the full matrix (lib/demand.ts `isGapAxis`) — never from a filtered view.
 *
 * With `onClick` it is a button that opens the axis-level demand detail; without, a plain key.
 * `no_signal` with no voices shows no count: "0 voices" beside it would read as a measured zero,
 * and the coverage line in the detail is where that denominator is honestly stated. A praise-only
 * axis is `no_signal` WITH voices, and those are counted like any others.
 *
 * `axis` names the capability in the button's accessible name — without it every row's button
 * reads identically to a screen reader.
 */
export function DemandChip({
  verdict,
  voices,
  gap,
  axis,
  onClick,
  pressed = false,
}: {
  verdict: DemandVerdict;
  voices: number;
  gap: boolean;
  axis?: string | undefined;
  onClick?: (() => void) | undefined;
  pressed?: boolean;
}): JSX.Element {
  const className = `key k-demand-${verdict}${isHeadlineDemand(verdict, gap) ? ' on-gap' : ''}`;
  const content = (
    <>
      {DEMAND_VERDICT_LABEL[verdict]}
      {(verdict !== 'no_signal' || voices > 0) && <span className="key-count">· {voicesLabel(voices)}</span>}
    </>
  );
  if (!onClick) return <span className={className}>{content}</span>;
  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      aria-pressed={pressed}
      aria-label={axis ? `Demand for ${axis}: ${DEMAND_VERDICT_LABEL[verdict]}${verdict !== 'no_signal' || voices > 0 ? `, ${voicesLabel(voices)}` : ''}` : undefined}
      title={verdict === 'no_signal' ? 'Demand: searched, nothing found — open for where we looked' : 'Demand: open the quotes and where we looked'}
    >
      {content}
    </button>
  );
}
